import { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandLockup } from "@/components/BrandMark";
import ThemeToggle from "@/components/ThemeToggle";

const navLinks = [
  { label: "Home", to: "/" },
  { label: "Products", to: "/products" },
  { label: "Shade Catalogue", to: "/catalogue" },
  { label: "Heritage", to: "/heritage" },
  { label: "Delivery", to: "/delivery" },
  { label: "Gallery", to: "/gallery" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const linkStyle = ({ isActive }) => ({
    fontFamily: "'Manrope', sans-serif",
    color: translucent
      ? (isActive ? "var(--mp-copper)" : "rgba(255,255,255,0.85)")
      : (isActive ? "var(--mp-text-strong)" : "var(--mp-muted)"),
    fontWeight: isActive ? 600 : 500,
  });

  // Floating navbar shows when not home, or when scrolled on home
  const translucent = isHome && !scrolled;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        translucent
          ? "bg-transparent"
          : "bg-white/90 backdrop-blur-xl shadow-sm border-b border-[var(--mp-line)]"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between gap-4 h-16 lg:h-20">
        <Link to="/" className="brand-link shrink-0" data-testid="navbar-logo">
          <BrandLockup reverse={translucent} animate />
          <span className="sr-only">Mutha Paras &amp; Co. — Pioneers in Lining Fabric Solutions Since 1974</span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-5 xl:gap-8 whitespace-nowrap" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              className="nav-link text-sm transition"
              style={linkStyle}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
            >
              {link.label}
            </NavLink>
          ))}
          <ThemeToggle onDark={translucent} />
          <Button
            onClick={() => navigate("/order")}
            className="text-sm font-medium px-5 py-2 rounded-sm"
            style={{
              backgroundColor: translucent ? "var(--mp-copper)" : "var(--mp-solid)",
              color: translucent ? "#101010" : "var(--mp-on-solid)",
              fontFamily: "'Manrope', sans-serif",
            }}
            data-testid="navbar-order-btn"
          >
            <ShoppingBag className="w-4 h-4 mr-2" />
            Order Now
          </Button>
        </nav>

        <div className="lg:hidden flex items-center gap-1">
          <ThemeToggle onDark={translucent} />
          <button
            className="p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? (
              <X className="w-6 h-6" style={{ color: translucent ? "var(--mp-copper)" : "var(--mp-text-strong)" }} />
            ) : (
              <Menu className="w-6 h-6" style={{ color: translucent ? "var(--mp-copper)" : "var(--mp-text-strong)" }} />
            )}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-[var(--mp-line)] px-6 py-6 space-y-3 animate-fade-in"
          data-testid="mobile-nav"
        >
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              className={({ isActive }) =>
                `block text-base py-2 ${isActive ? "font-semibold" : "font-medium"}`
              }
              style={({ isActive }) => ({
                fontFamily: "'Manrope', sans-serif",
                color: isActive ? "var(--mp-copper)" : "var(--mp-text)",
              })}
              data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
            >
              {link.label}
            </NavLink>
          ))}
          <Link to="/order" data-testid="mobile-order-btn">
            <Button
              className="w-full text-sm font-medium py-3 rounded-sm mt-2"
              style={{
                backgroundColor: "var(--mp-solid)",
                color: "var(--mp-on-solid)",
                fontFamily: "'Manrope', sans-serif",
              }}
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              Order Now
            </Button>
          </Link>
        </div>
      )}
    </header>
  );
}
